import {
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ReactNode } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, useOutletContext, useSearchParams } from "react-router";
import { Check, Copy, Pencil, Plus, RefreshCcw, Share2 } from "lucide-react";
import { toast } from "sonner";
import type { Brand } from "@/components/AppSidebar";
import {
  Conversation,
  ConversationContent,
} from "@/components/ai/conversation";
import { Loader } from "@/components/ai/loader";
import { Message, MessageContent } from "@/components/ai/message";
import {
  PromptInput,
  PromptInputButton,
  PromptInputSubmit,
  PromptInputTextarea,
  PromptInputToolbar,
  PromptInputTools,
} from "@/components/ai/prompt-input";
import { Suggestion, Suggestions } from "@/components/ai/suggestion";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { API_BASE_URL, dataChatAPI } from "@/services/api";
import type {
  DataChatAnswerComponent,
  DataChatEvidence,
  DataChatProgressPayload,
  DataChatRecommendedAction,
  DataChatStructuredPayload,
} from "@/services/api";
import type { DateRangeValue } from "@/components/DateRangeFilter";
import { useLocale } from "@/i18n/LocaleProvider";
import { capturePosthogEvent } from "@/lib/posthog";
import { useSocketStore } from "@/store/useSocketStore";
import agentLogo from "@/assets/ai-chat/logo.svg";
import fileLineChartIcon from "@/assets/ai-chat/file-line-chart.svg";
import lineChartIcon from "@/assets/ai-chat/icon.svg";
import trophyIcon from "@/assets/ai-chat/trophy.svg";
import wandSparklesIcon from "@/assets/ai-chat/wand-sparkles.svg";

type ChatMessage = {
  id?: string;
  role: "assistant" | "user";
  content: string;
  context?: DataChatStructuredPayload | null;
  isGenerating?: boolean;
};

type SendMessageVariables = {
  requestId: string;
  threadId: string;
  userMessageId: string;
  message: string;
  rerunFromMessageId?: string | null;
  signal?: AbortSignal;
};

type PendingMessageRequest = {
  requestId: string;
  originViewKey: string;
  threadId: string | null;
  message: string;
  abortController: AbortController | null;
  progressStage: "queued" | DataChatProgressPayload["stage"];
  progressDetail: string | null;
  progressTools: string[];
  completedTools: number | null;
  totalTools: number | null;
};

const dataChatProgressStageOrder: Record<
  PendingMessageRequest["progressStage"],
  number
> = {
  queued: 0,
  planning: 1,
  querying: 2,
  synthesizing: 3,
  completed: 4,
  failed: 4,
};

type MessageActionButtonProps = {
  label: string;
  onClick?: () => void;
  children: ReactNode;
  disabled?: boolean;
};

const markdownInlinePattern = /(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\([^)]+\))/g;

const normalizeAssistantMarkdown = (value: string) =>
  String(value || "").replace(/\r\n/g, "\n").trim();

const isRequestCanceled = (error: unknown) => {
  if (!error || typeof error !== "object") return false;
  const candidate = error as {
    code?: string;
    name?: string;
    message?: string;
  };
  return (
    candidate.code === "ERR_CANCELED" ||
    candidate.name === "CanceledError" ||
    candidate.name === "AbortError" ||
    candidate.message === "canceled"
  );
};

const splitMarkdownCells = (line: string) =>
  line
    .trim()
    .replace(/^\|/, "")
    .replace(/\|$/, "")
    .split("|")
    .map((cell) => cell.trim());

const isMarkdownTableSeparator = (line: string) =>
  /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?\s*$/.test(line);

const isMarkdownTableRow = (line: string) =>
  (line.match(/\|/g) || []).length >= 2;

const isMarkdownTableStart = (lines: string[], index: number) =>
  isMarkdownTableRow(lines[index] || "") &&
  Boolean(lines[index + 1]) &&
  isMarkdownTableSeparator(lines[index + 1]);

const isCompactTableValue = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) return true;
  return /^[\p{Emoji_Presentation}\p{Emoji}\s$€£¥₴₿₹₽+\-−.,:%/()0-9A-Za-z]{1,32}$/u.test(
    trimmed,
  );
};

const tableCellClassName = (value: string, index: number, isHeader = false) => {
  const base = isHeader
    ? "border-b border-[#E5E5E5] px-4 py-3 align-top font-semibold text-[#171717]"
    : "px-4 py-3 align-top text-[#404040]";
  const width =
    index === 0
      ? "min-w-[180px]"
      : isCompactTableValue(value)
        ? "min-w-[120px] whitespace-nowrap"
        : "min-w-[180px]";
  return `${base} ${width} break-words [overflow-wrap:anywhere]`;
};

function renderMarkdownInline(text: string, keyPrefix: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  let lastIndex = 0;
  let tokenIndex = 0;
  for (const match of text.matchAll(markdownInlinePattern)) {
    const token = match[0];
    const index = match.index ?? 0;
    if (index > lastIndex) {
      nodes.push(text.slice(lastIndex, index));
    }
    const key = `${keyPrefix}-inline-${tokenIndex}`;
    if (token.startsWith("**") && token.endsWith("**")) {
      nodes.push(
        <strong key={key} className="font-semibold text-[#171717]">
          {token.slice(2, -2)}
        </strong>,
      );
    } else if (token.startsWith("`") && token.endsWith("`")) {
      nodes.push(
        <code
          key={key}
          className="rounded bg-[#F5F5F5] px-1 py-0.5 font-mono text-[0.9em] text-[#171717]"
        >
          {token.slice(1, -1)}
        </code>,
      );
    } else {
      const link = token.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
      const href = link?.[2] || "";
      const isSafeHref = /^https?:\/\//i.test(href);
      nodes.push(
        isSafeHref ? (
          <a
            key={key}
            href={href}
            target="_blank"
            rel="noreferrer"
            className="font-medium text-[#0A0A0A] underline decoration-[#D4D4D4] underline-offset-2 hover:decoration-[#737373]"
          >
            {link?.[1] || href}
          </a>
        ) : (
          token
        ),
      );
    }
    lastIndex = index + token.length;
    tokenIndex += 1;
  }
  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }
  return nodes;
}

function renderAssistantMarkdown(content: string) {
  const normalized = normalizeAssistantMarkdown(content);
  if (!normalized) return null;
  const lines = normalized.split("\n");
  const blocks: ReactNode[] = [];
  const orderedListItemPattern = /^\s*\d+[.)]\s+/;
  let index = 0;
  let blockIndex = 0;

  const pushParagraph = (paragraphLines: string[]) => {
    const text = paragraphLines.join(" ").trim();
    if (!text) return;
    blocks.push(
      <p
        key={`md-p-${blockIndex}`}
        className="break-words leading-7 text-[#171717] [overflow-wrap:anywhere]"
      >
        {renderMarkdownInline(text, `md-p-${blockIndex}`)}
      </p>,
    );
    blockIndex += 1;
  };

  while (index < lines.length) {
    const line = lines[index];
    const trimmed = line.trim();
    if (!trimmed) {
      index += 1;
      continue;
    }

    if (/^-{3,}$/.test(trimmed)) {
      blocks.push(
        <hr key={`md-hr-${blockIndex}`} className="border-[#E5E5E5]" />,
      );
      blockIndex += 1;
      index += 1;
      continue;
    }

    const heading = trimmed.match(/^(#{1,4})\s+(.+)$/);
    if (heading) {
      const Tag = heading[1].length <= 2 ? "h3" : "h4";
      blocks.push(
        <Tag
          key={`md-h-${blockIndex}`}
          className="break-words text-base font-semibold leading-7 text-[#171717] [overflow-wrap:anywhere]"
        >
          {renderMarkdownInline(heading[2], `md-h-${blockIndex}`)}
        </Tag>,
      );
      blockIndex += 1;
      index += 1;
      continue;
    }

    if (isMarkdownTableStart(lines, index)) {
      const headers = splitMarkdownCells(lines[index]);
      index += 2;
      const rows: string[][] = [];
      while (
        index < lines.length &&
        isMarkdownTableRow(lines[index]) &&
        !isMarkdownTableSeparator(lines[index])
      ) {
        rows.push(splitMarkdownCells(lines[index]));
        index += 1;
      }
      blocks.push(
        <div
          key={`md-table-${blockIndex}`}
          className="max-w-full overflow-x-auto rounded-lg border border-[#E5E5E5]"
        >
          <table className="w-max min-w-full border-collapse text-left text-sm">
            <thead className="bg-[#FAFAFA]">
              <tr>
                {headers.map((header, headerIndex) => (
                  <th
                    key={`md-table-${blockIndex}-h-${headerIndex}`}
                    className={tableCellClassName(header, headerIndex, true)}
                  >
                    {renderMarkdownInline(
                      header,
                      `md-table-${blockIndex}-h-${headerIndex}`,
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIndex) => (
                <tr
                  key={`md-table-${blockIndex}-r-${rowIndex}`}
                  className="border-t border-[#F5F5F5]"
                >
                  {headers.map((_, cellIndex) => (
                    <td
                      key={`md-table-${blockIndex}-r-${rowIndex}-${cellIndex}`}
                      className={tableCellClassName(
                        row[cellIndex] || "",
                        cellIndex,
                      )}
                    >
                      {renderMarkdownInline(
                        row[cellIndex] || "",
                        `md-table-${blockIndex}-r-${rowIndex}-${cellIndex}`,
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
      blockIndex += 1;
      continue;
    }

    if (/^\s*[-*]\s+/.test(line)) {
      const items: string[] = [];
      while (index < lines.length && /^\s*[-*]\s+/.test(lines[index])) {
        items.push(lines[index].replace(/^\s*[-*]\s+/, "").trim());
        index += 1;
      }
      blocks.push(
        <ul
          key={`md-ul-${blockIndex}`}
          className="list-disc space-y-1 pl-5 leading-7 text-[#171717] marker:text-[#A3A3A3]"
        >
          {items.map((item, itemIndex) => (
            <li
              key={`md-ul-${blockIndex}-${itemIndex}`}
              className="break-words [overflow-wrap:anywhere]"
            >
              {renderMarkdownInline(item, `md-ul-${blockIndex}-${itemIndex}`)}
            </li>
          ))}
        </ul>,
      );
      blockIndex += 1;
      continue;
    }

    if (orderedListItemPattern.test(line)) {
      const items: string[] = [];
      while (index < lines.length) {
        if (orderedListItemPattern.test(lines[index])) {
          items.push(lines[index].replace(orderedListItemPattern, "").trim());
          index += 1;
          continue;
        }

        let nextIndex = index;
        while (nextIndex < lines.length && !lines[nextIndex].trim()) {
          nextIndex += 1;
        }
        if (
          nextIndex < lines.length &&
          orderedListItemPattern.test(lines[nextIndex])
        ) {
          index = nextIndex;
          continue;
        }
        break;
      }
      blocks.push(
        <ol
          key={`md-ol-${blockIndex}`}
          className="list-decimal space-y-1 pl-5 leading-7 text-[#171717] marker:text-[#A3A3A3]"
        >
          {items.map((item, itemIndex) => (
            <li
              key={`md-ol-${blockIndex}-${itemIndex}`}
              className="break-words [overflow-wrap:anywhere]"
            >
              {renderMarkdownInline(item, `md-ol-${blockIndex}-${itemIndex}`)}
            </li>
          ))}
        </ol>,
      );
      blockIndex += 1;
      continue;
    }

    const paragraphLines = [line];
    index += 1;
    while (
      index < lines.length &&
      lines[index].trim() &&
      !/^-{3,}$/.test(lines[index].trim()) &&
      !/^(#{1,4})\s+/.test(lines[index].trim()) &&
      !isMarkdownTableStart(lines, index) &&
      !/^\s*[-*]\s+/.test(lines[index]) &&
      !orderedListItemPattern.test(lines[index])
    ) {
      paragraphLines.push(lines[index]);
      index += 1;
    }
    pushParagraph(paragraphLines);
  }

  return <div className="space-y-3">{blocks}</div>;
}

function MessageActionButton({
  label,
  onClick,
  children,
  disabled = false,
}: MessageActionButtonProps) {
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const openTimerRef = useRef<number | null>(null);
  const tooltipId = useId();
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState<{
    left: number;
    top: number;
  } | null>(null);

  const clearOpenTimer = () => {
    if (openTimerRef.current !== null) {
      window.clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
  };

  const updateTooltipPosition = () => {
    const button = buttonRef.current;
    if (!button) return;
    const rect = button.getBoundingClientRect();
    setTooltipPosition({
      left: rect.left + rect.width / 2,
      top: rect.bottom + 8,
    });
  };

  const openTooltip = (delay = 250) => {
    if (disabled) return;
    clearOpenTimer();
    openTimerRef.current = window.setTimeout(() => {
      updateTooltipPosition();
      setTooltipOpen(true);
      openTimerRef.current = null;
    }, delay);
  };

  const closeTooltip = () => {
    clearOpenTimer();
    setTooltipOpen(false);
  };

  useEffect(() => {
    if (!tooltipOpen) return undefined;
    const syncPosition = () => updateTooltipPosition();
    window.addEventListener("scroll", syncPosition, true);
    window.addEventListener("resize", syncPosition);
    return () => {
      window.removeEventListener("scroll", syncPosition, true);
      window.removeEventListener("resize", syncPosition);
    };
  }, [tooltipOpen]);

  useEffect(() => {
    return () => clearOpenTimer();
  }, []);

  return (
    <span className="inline-flex">
      <button
        ref={buttonRef}
        type="button"
        aria-label={label}
        aria-describedby={tooltipOpen ? tooltipId : undefined}
        aria-disabled={disabled}
        onPointerEnter={() => openTooltip()}
        onPointerLeave={closeTooltip}
        onFocus={() => openTooltip(0)}
        onBlur={closeTooltip}
        onKeyDown={(event) => {
          if (event.key === "Escape") closeTooltip();
        }}
        onClick={() => {
          if (disabled) return;
          closeTooltip();
          onClick?.();
        }}
        className={`inline-flex h-8 min-w-8 items-center justify-center rounded-lg px-2 text-[#525252] transition-colors hover:bg-[#F5F5F5] hover:text-[#171717] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF5E00]/30 ${
          disabled ? "cursor-not-allowed opacity-60" : ""
        }`}
      >
        {children}
      </button>
      {tooltipOpen && tooltipPosition && typeof document !== "undefined"
        ? createPortal(
            <div
              id={tooltipId}
              role="tooltip"
              className="pointer-events-none z-50 w-max max-w-[180px] rounded-md bg-[#171717] px-3 py-1.5 text-xs font-medium leading-none text-white shadow-sm"
              style={{
                position: "fixed",
                left: tooltipPosition.left,
                top: tooltipPosition.top,
                transform: "translateX(-50%)",
              }}
            >
              {label}
            </div>,
            document.body,
          )
        : null}
    </span>
  );
}

const starterSuggestions = [
  {
    labelKey: "dataChat.starter.visibilityGaps",
    prompt:
      "Analyze my AI visibility gaps using Nobori data only. Show where my brand is underperforming across prompts, clusters, AI platforms, countries, and competitors. Highlight the biggest gaps, explain what is driving them, and prioritize the areas I should review first.",
    icon: lineChartIcon,
  },
  {
    labelKey: "dataChat.starter.citationGaps",
    prompt:
      "Find citation gaps for my brand using Nobori data only. Show which cited pages and domains mention competitors but not us, which sources appear most often in AI answers, and which citation opportunities are most important based on prompt and competitor visibility data.",
    icon: wandSparklesIcon,
  },
  {
    labelKey: "dataChat.starter.competitors",
    prompt:
      "Analyze my top competitors. Compare their AI visibility, share of voice, prompt coverage, cluster performance, citation presence, and recent movement against my brand. Highlight the strongest competitor, where they outperform us, and where we have the biggest opportunity to close the gap.",
    icon: trophyIcon,
  },
  {
    labelKey: "dataChat.starter.recentChanges",
    prompt:
      "Analyze my visibility gaps. Show where my brand is underperforming across prompts, clusters, AI platforms, countries, and competitors. Highlight the biggest gaps and prioritize what I should review first.",
    icon: fileLineChartIcon,
  },
];

const emptyStructuredPayload = (): DataChatStructuredPayload => ({
  recommendedActions: [],
  evidence: [],
  dataLimitations: [],
  followUpQuestions: [],
  components: [],
});

const normalizeChatText = (value: unknown) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");

const stripSuggestedActionPunctuation = (value: unknown) =>
  String(value || "")
    .trim()
    .replace(/[.!?。！？]+$/g, "")
    .trim();

const unavailableDisplayValues = new Set([
  "",
  "n/a",
  "na",
  "none",
  "null",
  "undefined",
  "unavailable",
  "no data",
  "[object object]",
]);

const formatStructuredDisplayValue = (value: unknown): string | number => {
  if (typeof value === "number") return value;
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (value === null || value === undefined) return "No data";
  if (typeof value === "string") {
    const trimmed = value.trim();
    return unavailableDisplayValues.has(trimmed.toLowerCase())
      ? "No data"
      : trimmed;
  }
  if (Array.isArray(value)) {
    const rendered = value
      .map(formatStructuredDisplayValue)
      .filter(
        (item) =>
          !unavailableDisplayValues.has(String(item).trim().toLowerCase()),
      )
      .join(", ");
    return rendered || "No data";
  }
  if (typeof value === "object") {
    const record = value as Record<string, unknown>;
    const primary =
      record.title ??
      record.name ??
      record.label ??
      record.type ??
      record.category ??
      record.priority ??
      record.status ??
      record.value ??
      record.count ??
      record.description ??
      record.reason ??
      record.summary;
    const primaryText = formatStructuredDisplayValue(primary);
    if (
      !unavailableDisplayValues.has(String(primaryText).trim().toLowerCase())
    ) {
      return primaryText;
    }
  }
  return "No data";
};

const isAvailableStructuredValue = (value: unknown) => {
  const rendered = formatStructuredDisplayValue(value);
  return !unavailableDisplayValues.has(String(rendered).trim().toLowerCase());
};

const formatStructuredColumnLabel = (value: unknown) => {
  const normalized = String(value || "")
    .trim()
    .replace(/[_-]+/g, " ");
  const lower = normalized.toLowerCase();
  const labels: Record<string, string> = {
    title: "Item",
    reason: "Why it matters",
    url: "URL",
    domain: "Domain",
    source: "Source",
    priority: "Priority",
    status: "Status",
  };
  if (labels[lower]) return labels[lower];
  return normalized.replace(/\b\w/g, (char) => char.toUpperCase());
};

const shouldRenderAnswerComponent = (component: DataChatAnswerComponent) => {
  if (component.type === "key_takeaway") return false;
  if (component.rows?.length) {
    return component.rows.some((row) =>
      Object.values(row).some(isAvailableStructuredValue),
    );
  }
  if (component.items?.length) {
    return component.items.some(isAvailableStructuredValue);
  }
  if (component.metrics?.length) {
    return component.metrics.some(
      (metric) =>
        isAvailableStructuredValue(metric.value) ||
        isAvailableStructuredValue(metric.detail),
    );
  }
  if (component.type !== "opportunity") {
    return Boolean(component.summary);
  }
  const searchable = normalizeChatText(
    `${component.title} ${component.summary || ""}`,
  );
  return !(
    searchable.includes("getcitedontopfsmlisticles") ||
    searchable.includes("canonicalfieldservicemanagementlisticle") ||
    searchable.includes("publishsupportinglisticlesvideos") ||
    searchable.includes("addusefulforumcommunityreplies")
  );
};

const shouldRenderDataLimitation = (value: string) => {
  const searchable = normalizeChatText(value);
  return ![
    "suppliedvisibilitycontext",
    "noteverypossibleprompt",
    "truncatedplaceholder",
    "internalids",
    "trenddirectionisunknown",
    "doesnotshowcausation",
    "notshowwhichmissingprompts",
    "promptnamesareuserfacing",
    "nopromptlevelgapsweredetected",
    "nocitationopportunitieswereavailable",
    "notenoughchartpoints",
    "onlycompletedpromptresultsareincluded",
    "pendingexecutionsexist",
    "somepromptexecutionsarestillpending",
    "queryfailed",
    "queryerror",
    "datelabelsunavailable",
    "datelabelsonstoredsnapshotrowswereunavailable",
    "datefieldisempty",
    "daybydaybreakdownisunavailable",
    "rawgscrows",
    "groupbyissue",
    "breakdowndatareflectsapproximaterecentperiod",
    "approximaterecentperiodonly",
    "backend",
    "internalerror",
  ].some((pattern) => searchable.includes(pattern));
};

const responseContext = (response: {
  recommendedActions?: DataChatRecommendedAction[];
  evidence?: DataChatEvidence[];
  dataLimitations?: string[];
  followUpQuestions?: string[];
  components?: DataChatAnswerComponent[];
}): DataChatStructuredPayload => ({
  recommendedActions: response.recommendedActions || [],
  evidence: [],
  dataLimitations: (response.dataLimitations || []).filter(
    shouldRenderDataLimitation,
  ),
  followUpQuestions: (response.followUpQuestions || [])
    .map(stripSuggestedActionPunctuation)
    .filter(Boolean),
  components: [],
});

const getAnswerAnalysisStepKey = (message: string) => {
  const normalized = message.toLowerCase();
  if (/\b(cluster|clusters)\b/.test(normalized)) {
    return "dataChat.loading.checkingClusters";
  }
  if (
    /\b(citation|citations|cited|domain|domains|page|pages|source|sources)\b/.test(
      normalized,
    )
  ) {
    return "dataChat.loading.reviewingCitations";
  }
  if (
    /\b(competitor|competitors|outrank|ahead|behind|beat)\b/.test(normalized)
  ) {
    return "dataChat.loading.comparingCompetitors";
  }
  if (/\b(task|tasks|open|completed|priority|prioritize)\b/.test(normalized)) {
    return "dataChat.loading.checkingTasks";
  }
  if (/\b(prompt|prompts|question|questions)\b/.test(normalized)) {
    return "dataChat.loading.reviewingPrompts";
  }
  if (
    /\b(visibility|visible|gap|gaps|change|changed|trend|share of voice|sov)\b/.test(
      normalized,
    )
  ) {
    return "dataChat.loading.reviewingVisibility";
  }
  return "dataChat.loading.reviewingRelevant";
};

const answerRevealIntervalMs = 38;
const answerRevealMinChunkSize = 8;
const normalizeUserMessageContent = (content: string) => content.trim();

export default function DataChatPage() {
  const { t } = useLocale();
  const { currentBrand, selectedProviders, timeRange, dateRange } =
    useOutletContext<{
      currentBrand: Brand;
      selectedProviders?: string[];
      timeRange: string;
      dateRange?: DateRangeValue;
    }>();
  const brandId = currentBrand?.brandId || currentBrand?.id;
  const days = dateRange?.days || Number.parseInt(timeRange || "30", 10) || 30;
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [editingMessageIndex, setEditingMessageIndex] = useState<number | null>(
    null,
  );
  const [editingDraft, setEditingDraft] = useState("");
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [placeholderText, setPlaceholderText] = useState("");
  const [placeholderMode, setPlaceholderMode] = useState<
    "typing" | "pausing" | "deleting"
  >("typing");
  const [pendingMessageRequests, setPendingMessageRequests] = useState<
    PendingMessageRequest[]
  >([]);
  const [isAnswerRevealing, setIsAnswerRevealing] = useState(false);
  const [copiedActionId, setCopiedActionId] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const queryClient = useQueryClient();
  const ensureSocket = useSocketStore((state) => state.ensureSocket);
  const addVisibilitySubscription = useSocketStore(
    (state) => state.addVisibilitySubscription,
  );
  const removeVisibilitySubscription = useSocketStore(
    (state) => state.removeVisibilitySubscription,
  );
  const threadId = searchParams.get("threadId");
  const currentViewKey = `${brandId || "no-brand"}:${
    threadId ? `thread:${threadId}` : `new:${location.key}`
  }`;
  const currentViewKeyRef = useRef(currentViewKey);
  currentViewKeyRef.current = currentViewKey;
  const lastSentAtRef = useRef<number | null>(null);
  const conversationEndRef = useRef<HTMLDivElement | null>(null);
  const editingTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const copiedResetTimeoutRef = useRef<number | null>(null);
  const answerRevealIntervalRef = useRef<number | null>(null);
  const answerRevealActiveRef = useRef(false);
  const pendingMessageRequestsRef = useRef(pendingMessageRequests);
  pendingMessageRequestsRef.current = pendingMessageRequests;
  const currentPendingRequest = pendingMessageRequests.find((request) =>
    request.threadId
      ? request.threadId === threadId
      : request.originViewKey === currentViewKey,
  );
  const brandName = currentBrand?.name || "your brand";
  const animatedPlaceholders = useMemo(
    () => [
      t("dataChat.placeholder.ask"),
      t("dataChat.placeholder.summarize", { brandName, days }),
      t("dataChat.placeholder.citationGaps"),
      t("dataChat.placeholder.compareCompetitors"),
    ],
    [brandName, days, t],
  );
  const answerLoadingSteps = useMemo(() => {
    const sourceLabel = currentPendingRequest?.progressTools.length
      ? currentPendingRequest.progressTools.join(", ")
      : null;
    return [
      t("dataChat.loading.savingQuestion"),
      t("dataChat.loading.planning"),
      sourceLabel
        ? t("dataChat.loading.queryingSources", { sources: sourceLabel })
        : t(getAnswerAnalysisStepKey(currentPendingRequest?.message || "")),
      t("dataChat.loading.preparing"),
    ];
  }, [currentPendingRequest?.message, currentPendingRequest?.progressTools, t]);
  const answerLoadingStep = currentPendingRequest
    ? {
        queued: 0,
        planning: 1,
        querying: 2,
        synthesizing: 3,
        completed: 4,
        failed: 3,
      }[currentPendingRequest.progressStage]
    : 0;

  useEffect(() => {
    if (!brandId) return;
    const signature = `data-chat-progress:${brandId}`;
    const socket = ensureSocket(API_BASE_URL);
    addVisibilitySubscription(signature, {
      brandId,
      days: [1, 7, 14, 30, 90].includes(days) ? String(days) : "30",
    });
    const handleProgress = (payload: DataChatProgressPayload) => {
      if (payload?.brandId !== brandId || !payload.requestId) return;
      setPendingMessageRequests((current) =>
        current.map((request) =>
          request.requestId === payload.requestId &&
          dataChatProgressStageOrder[payload.stage] >=
            dataChatProgressStageOrder[request.progressStage]
            ? {
                ...request,
                progressStage: payload.stage,
                progressDetail: payload.detail || null,
                progressTools: Array.isArray(payload.tools)
                  ? payload.tools
                  : request.progressTools,
                completedTools:
                  typeof payload.completedTools === "number"
                    ? payload.completedTools
                    : request.completedTools,
                totalTools:
                  typeof payload.totalTools === "number"
                    ? payload.totalTools
                    : request.totalTools,
              }
            : request,
        ),
      );
    };
    socket.on("data-chat:progress", handleProgress);
    return () => {
      socket.off("data-chat:progress", handleProgress);
      removeVisibilitySubscription(signature);
    };
  }, [
    addVisibilitySubscription,
    brandId,
    days,
    ensureSocket,
    removeVisibilitySubscription,
  ]);

  const trackAgentEvent = (
    event: string,
    properties?: Record<string, unknown>,
  ) => {
    capturePosthogEvent(event, {
      company_id: (currentBrand as any)?.companyId || null,
      brand_id: brandId || null,
      chat_id: threadId || null,
      ...properties,
    });
  };

  const threadQuery = useQuery({
    queryKey: ["data-chat-thread", brandId, threadId],
    queryFn: async () => {
      if (!brandId || !threadId) {
        throw new Error("Missing chat thread");
      }
      return dataChatAPI.getThread(brandId, threadId);
    },
    enabled: Boolean(brandId && threadId),
    staleTime: 30_000,
  });

  useEffect(() => {
    stopAnswerReveal();
    if (!threadId) {
      setMessages([]);
    }
  }, [brandId, threadId]);

  useEffect(() => {
    if (!brandId) return;
    trackAgentEvent("agent_opened");
    // Track once per selected brand/view mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandId]);

  useEffect(() => {
    if (!brandId || !threadId) return;
    trackAgentEvent("agent_chat_opened", { chat_id: threadId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandId, threadId]);

  useEffect(() => {
    if (!threadQuery.data?.messages) return;
    const savedMessages = threadQuery.data.messages;
    const lastMessage = savedMessages.at(-1);
    const hasPersistedPendingAnswer =
      lastMessage?.role === "user" &&
      lastMessage.context?.generationStatus === "pending";
    const persistedRequestId = lastMessage?.context?.requestId || null;

    if (hasPersistedPendingAnswer) {
      setPendingMessageRequests((current) => {
        if (
          current.some(
            (request) =>
              request.threadId === threadId ||
              (persistedRequestId && request.requestId === persistedRequestId),
          )
        ) {
          return current;
        }
        return [
          ...current,
          {
            requestId:
              persistedRequestId || `restored-${threadId}-${lastMessage.id}`,
            originViewKey: currentViewKey,
            threadId,
            message: lastMessage.content,
            abortController: null,
            progressStage: "planning",
            progressDetail: null,
            progressTools: [],
            completedTools: null,
            totalTools: null,
          },
        ];
      });
    } else {
      setPendingMessageRequests((current) =>
        current.filter(
          (request) =>
            request.threadId !== threadId || request.abortController !== null,
        ),
      );
    }

    const hasLocalPendingRequest = pendingMessageRequestsRef.current.some(
      (request) =>
        request.threadId === threadId && request.abortController !== null,
    );
    if (hasLocalPendingRequest || answerRevealActiveRef.current) return;
    stopAnswerReveal();
    setMessages(
      savedMessages.map((message) => ({
        id: message.id,
        role: message.role,
        content:
          message.role === "user"
            ? normalizeUserMessageContent(message.content)
            : message.content,
        context: message.context ? responseContext(message.context) : null,
      })),
    );
  }, [currentViewKey, threadId, threadQuery.data?.messages]);

  useEffect(() => {
    if (!currentPendingRequest || currentPendingRequest.abortController) return;
    const interval = window.setInterval(() => {
      void threadQuery.refetch();
    }, 2000);
    return () => window.clearInterval(interval);
  }, [
    currentPendingRequest?.abortController,
    currentPendingRequest?.requestId,
    threadQuery.refetch,
  ]);

  useEffect(() => {
    if (!threadQuery.error) return;
    toast.error(t("dataChat.toast.loadFailed"));
  }, [threadQuery.error, t]);

  useEffect(() => {
    setPlaceholderIndex(0);
    setPlaceholderText("");
    setPlaceholderMode("typing");
  }, [animatedPlaceholders]);

  useEffect(() => {
    if (input.trim()) return;
    const currentPlaceholder =
      animatedPlaceholders[placeholderIndex % animatedPlaceholders.length] ||
      "";
    const delay =
      placeholderMode === "pausing"
        ? 1200
        : placeholderMode === "deleting"
          ? 18
          : 34;
    const timeout = window.setTimeout(() => {
      if (placeholderMode === "typing") {
        if (placeholderText.length < currentPlaceholder.length) {
          setPlaceholderText(
            currentPlaceholder.slice(0, placeholderText.length + 1),
          );
          return;
        }
        setPlaceholderMode("pausing");
        return;
      }
      if (placeholderMode === "pausing") {
        setPlaceholderMode("deleting");
        return;
      }
      if (placeholderText.length > 0) {
        setPlaceholderText(placeholderText.slice(0, -1));
        return;
      }
      setPlaceholderIndex(
        (current) => (current + 1) % animatedPlaceholders.length,
      );
      setPlaceholderMode("typing");
    }, delay);
    return () => window.clearTimeout(timeout);
  }, [
    animatedPlaceholders,
    input,
    placeholderIndex,
    placeholderMode,
    placeholderText,
  ]);

  const scrollConversationToBottom = (behavior: ScrollBehavior = "smooth") => {
    conversationEndRef.current?.scrollIntoView({
      behavior,
      block: "end",
    });
  };

  const stopAnswerReveal = (
    options: { completeGeneratingMessage?: boolean } = {},
  ) => {
    if (answerRevealIntervalRef.current !== null) {
      window.clearInterval(answerRevealIntervalRef.current);
      answerRevealIntervalRef.current = null;
    }
    answerRevealActiveRef.current = false;
    setIsAnswerRevealing(false);
    if (options.completeGeneratingMessage) {
      setMessages((current) => {
        const next = [...current];
        for (let index = next.length - 1; index >= 0; index -= 1) {
          if (next[index].role === "assistant" && next[index].isGenerating) {
            next[index] = { ...next[index], isGenerating: false };
            return next;
          }
        }
        return current;
      });
    }
  };

  const revealAssistantAnswer = (
    answer: string,
    context: DataChatStructuredPayload,
    messageId?: string | null,
  ) => {
    stopAnswerReveal();
    if (!answer) {
      setIsAnswerRevealing(false);
      setMessages((current) => [
        ...current,
        { id: messageId ?? undefined, role: "assistant", content: "", context },
      ]);
      window.requestAnimationFrame(() => scrollConversationToBottom("auto"));
      return;
    }

    let visibleLength = 0;
    const chunkSize = Math.max(
      answerRevealMinChunkSize,
      Math.ceil(answer.length / 120),
    );
    answerRevealActiveRef.current = true;
    setIsAnswerRevealing(true);
    setMessages((current) => [
      ...current,
      {
        id: messageId ?? undefined,
        role: "assistant",
        content: "",
        context,
        isGenerating: true,
      },
    ]);
    window.requestAnimationFrame(() => scrollConversationToBottom("auto"));

    answerRevealIntervalRef.current = window.setInterval(() => {
      visibleLength = Math.min(answer.length, visibleLength + chunkSize);
      const isComplete = visibleLength >= answer.length;
      setMessages((current) => {
        const next = [...current];
        let messageIndex = -1;
        for (let index = next.length - 1; index >= 0; index -= 1) {
          if (next[index].role === "assistant" && next[index].isGenerating) {
            messageIndex = index;
            break;
          }
        }
        if (messageIndex === -1) return current;
        next[messageIndex] = {
          ...next[messageIndex],
          content: answer.slice(0, visibleLength),
          isGenerating: !isComplete,
        };
        return next;
      });
      window.requestAnimationFrame(() => scrollConversationToBottom("auto"));

      if (isComplete) {
        stopAnswerReveal();
      }
    }, answerRevealIntervalMs);
  };

  const sendMessage = useMutation({
    mutationFn: async (variables: SendMessageVariables) => {
      if (!brandId) throw new Error(t("dataChat.error.selectBrand"));
      return dataChatAPI.sendMessage({
        requestId: variables.requestId,
        brandId,
        threadId: variables.threadId,
        userMessageId: variables.userMessageId,
        rerunFromMessageId: variables.rerunFromMessageId,
        message: variables.message,
        providers: selectedProviders,
        days,
        signal: variables.signal,
      });
    },
    onSuccess: (response, variables) => {
      if (variables.signal?.aborted) return;
      const responseContextPayload = responseContext(response);
      const assistantMessageId =
        response.messages
          ?.filter((message) => message.role === "assistant")
          .at(-1)?.id ?? null;
      const responseThreadId = response.thread?.id || variables.threadId;
      if (
        currentViewKeyRef.current ===
        `${brandId || "no-brand"}:thread:${responseThreadId}`
      ) {
        revealAssistantAnswer(
          response.answer,
          responseContextPayload,
          assistantMessageId,
        );
      }
      const latencyMs =
        lastSentAtRef.current === null
          ? null
          : Date.now() - lastSentAtRef.current;
      trackAgentEvent("agent_answer_generated", {
        chat_id: responseThreadId,
        message_id: response.messages?.at(-1)?.id || null,
        question_type: null,
        data_sources_used: ["nobori_visibility"],
        has_missing_data: responseContextPayload.dataLimitations.length > 0,
        has_pending_runs: responseContextPayload.dataLimitations.some((item) =>
          item.toLowerCase().includes("pending"),
        ),
        answer_components_used: responseContextPayload.components.map(
          (component) => component.type,
        ),
        model_used: null,
        latency_ms: latencyMs,
      });
      if (response.answer.includes("cannot make changes yet")) {
        trackAgentEvent("agent_unsupported_action_requested", {
          chat_id: responseThreadId,
        });
      }
      if (brandId) {
        queryClient.invalidateQueries({
          queryKey: ["data-chat-threads", brandId],
        });
      }
      if (brandId && response.thread?.id) {
        queryClient.invalidateQueries({
          queryKey: ["data-chat-thread", brandId, response.thread.id],
        });
      }
    },
    onError: (error, variables) => {
      if (variables.signal?.aborted || isRequestCanceled(error)) return;
      trackAgentEvent("agent_error_shown", { error_type: "message_failed" });
      toast.error(t("dataChat.toast.answerFailed"));
    },
    onSettled: (_data, _error, variables) => {
      setPendingMessageRequests((current) =>
        current.filter((request) => request.requestId !== variables.requestId),
      );
    },
  });

  useEffect(() => {
    scrollConversationToBottom("smooth");
  }, [messages.length, currentPendingRequest, threadQuery.isLoading]);

  useLayoutEffect(() => {
    const textarea = editingTextareaRef.current;
    if (editingMessageIndex === null || !textarea) return;
    textarea.style.height = "auto";
    textarea.style.height = `${textarea.scrollHeight}px`;
  }, [editingDraft, editingMessageIndex]);

  useEffect(() => {
    return () => {
      if (copiedResetTimeoutRef.current !== null) {
        window.clearTimeout(copiedResetTimeoutRef.current);
      }
      pendingMessageRequestsRef.current.forEach((request) =>
        request.abortController?.abort(),
      );
      stopAnswerReveal();
    };
  }, []);

  const submitMessage = (
    rawMessage = input,
    options: {
      appendUserMessage?: boolean;
      rerunFromMessageId?: string | null;
    } = {},
  ) => {
    const message = rawMessage.trim();
    if (!message || currentPendingRequest || isAnswerRevealing) return;
    setInput("");
    if (options.appendUserMessage !== false) {
      setMessages((current) => [
        ...current,
        { role: "user", content: message },
      ]);
    }
    lastSentAtRef.current = Date.now();
    trackAgentEvent("agent_message_sent", {
      question_type: null,
      has_pending_runs: null,
      is_retry: Boolean(options.rerunFromMessageId),
    });
    const abortController = new AbortController();
    const requestId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const originViewKey = currentViewKey;
    setPendingMessageRequests((current) => [
      ...current,
      {
        requestId,
        originViewKey,
        threadId,
        message,
        abortController,
        progressStage: "queued",
        progressDetail: null,
        progressTools: [],
        completedTools: null,
        totalTools: null,
      },
    ]);

    void (async () => {
      try {
        if (!brandId) throw new Error(t("dataChat.error.selectBrand"));
        const threadListQueryKey = ["data-chat-threads", brandId] as const;
        await queryClient.cancelQueries({
          queryKey: threadListQueryKey,
          exact: true,
        });
        const started = await dataChatAPI.startMessage({
          brandId,
          requestId,
          threadId,
          rerunFromMessageId: options.rerunFromMessageId,
          message,
        });
        const savedThreadId = started.thread.id;
        const savedViewKey = `${brandId}:thread:${savedThreadId}`;
        queryClient.setQueryData(
          ["data-chat-thread", brandId, savedThreadId],
          (
            current:
              | { ok: boolean; thread: typeof started.thread; messages: any[] }
              | undefined,
          ) => ({
            ok: true,
            thread: started.thread,
            messages: current?.messages?.some(
              (savedMessage) => savedMessage.id === started.message.id,
            )
              ? current.messages
              : [...(current?.messages || []), started.message],
          }),
        );
        queryClient.setQueryData(
          threadListQueryKey,
          (
            current:
              | { ok: boolean; threads: Array<typeof started.thread> }
              | undefined,
          ) => ({
            ok: true,
            threads: [
              started.thread,
              ...(current?.threads || []).filter(
                (savedThread) => savedThread.id !== savedThreadId,
              ),
            ],
          }),
        );
        setPendingMessageRequests((current) =>
          current.map((request) =>
            request.requestId === requestId
              ? { ...request, threadId: savedThreadId }
              : request,
          ),
        );
        if (currentViewKeyRef.current === originViewKey) {
          currentViewKeyRef.current = savedViewKey;
          const nextParams = new URLSearchParams(searchParams);
          nextParams.set("brandId", brandId);
          nextParams.set("threadId", savedThreadId);
          setSearchParams(nextParams, { replace: true });
        }
        if (abortController.signal.aborted) {
          setPendingMessageRequests((current) =>
            current.filter((request) => request.requestId !== requestId),
          );
          return;
        }
        sendMessage.mutate({
          requestId,
          threadId: savedThreadId,
          userMessageId: started.message.id,
          message,
          rerunFromMessageId: options.rerunFromMessageId,
          signal: abortController.signal,
        });
      } catch (error) {
        setPendingMessageRequests((current) =>
          current.filter((request) => request.requestId !== requestId),
        );
        if (abortController.signal.aborted || isRequestCanceled(error)) return;
        trackAgentEvent("agent_error_shown", { error_type: "message_failed" });
        toast.error(t("dataChat.toast.answerFailed"));
      }
    })();
  };

  const stopCurrentResponse = () => {
    if (currentPendingRequest) {
      currentPendingRequest.abortController?.abort();
      setPendingMessageRequests((current) =>
        current.filter(
          (request) => request.requestId !== currentPendingRequest.requestId,
        ),
      );
      trackAgentEvent("agent_response_stopped", { stage: "request" });
      return;
    }

    if (answerRevealActiveRef.current || isAnswerRevealing) {
      stopAnswerReveal({ completeGeneratingMessage: true });
      trackAgentEvent("agent_response_stopped", { stage: "reveal" });
    }
  };

  const markActionCopied = (actionId: string) => {
    setCopiedActionId(actionId);
    if (copiedResetTimeoutRef.current !== null) {
      window.clearTimeout(copiedResetTimeoutRef.current);
    }
    copiedResetTimeoutRef.current = window.setTimeout(() => {
      setCopiedActionId((current) => (current === actionId ? null : current));
      copiedResetTimeoutRef.current = null;
    }, 1600);
  };

  const renderAnswerComponents = (components: DataChatAnswerComponent[]) => {
    if (!components.length) return null;
    return (
      <div className="space-y-3">
        {components.map((component, componentIndex) => {
          const key = `${component.type}-${component.title}-${componentIndex}`;
          if (
            component.type === "metric_comparison" &&
            component.metrics?.length
          ) {
            return (
              <div
                key={key}
                className="min-w-0 rounded-lg border border-[#E5E5E5] bg-[#FAFAFA] p-3"
              >
                <div className="text-sm font-semibold leading-5 text-[#171717]">
                  {component.title}
                </div>
                <div className="mt-3 grid min-w-0 gap-2 sm:grid-cols-2">
                  {component.metrics.map((metric) => (
                    <div
                      key={`${key}-${metric.label}`}
                      className="min-w-0 rounded-md border border-[#E5E5E5] bg-white px-3 py-2"
                    >
                      <div className="text-[11px] font-medium uppercase leading-4 tracking-normal text-[#737373]">
                        {metric.label}
                      </div>
                      <div className="mt-1 break-words text-sm font-semibold leading-5 text-[#171717] [overflow-wrap:anywhere]">
                        {metric.value}
                      </div>
                      {metric.detail ? (
                        <div className="mt-1 break-words text-xs leading-5 text-[#737373] [overflow-wrap:anywhere]">
                          {metric.detail}
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>
              </div>
            );
          }

          if (component.rows?.length) {
            const columns = component.columns?.length
              ? component.columns
              : Object.keys(component.rows[0] || {});
            const primaryColumn = columns[0] ?? "";
            const detailColumns = columns.slice(1);
            return (
              <div
                key={key}
                className="min-w-0 overflow-hidden rounded-lg border border-[#E5E5E5] bg-white"
              >
                <div className="border-b border-[#E5E5E5] bg-[#FAFAFA] px-3 py-2 text-sm font-semibold leading-5 text-[#171717]">
                  {component.title}
                </div>
                <div className="divide-y divide-[#F5F5F5]">
                  {component.rows.map((row, rowIndex) => {
                    const primaryValue = formatStructuredDisplayValue(
                      row[primaryColumn],
                    );
                    return (
                      <div
                        key={`${key}-${rowIndex}`}
                        className="min-w-0 px-3 py-3"
                      >
                        <div className="break-words text-sm font-semibold leading-5 text-[#171717] [overflow-wrap:anywhere]">
                          {primaryValue}
                        </div>
                        {detailColumns.length ? (
                          <div className="mt-2 grid min-w-0 gap-2 sm:grid-cols-2">
                            {detailColumns.map((column) => (
                              <div
                                key={`${key}-${rowIndex}-${column}`}
                                className="min-w-0"
                              >
                                <div className="text-[11px] font-medium uppercase leading-4 tracking-normal text-[#737373]">
                                  {formatStructuredColumnLabel(column)}
                                </div>
                                <div className="mt-0.5 break-words text-sm leading-5 text-[#404040] [overflow-wrap:anywhere]">
                                  {formatStructuredDisplayValue(row[column])}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          }

          const cardClass =
            component.type === "warning"
              ? "border-[#FACC15]/50 bg-[#FEFCE8]"
              : component.type === "opportunity"
                ? "border-[#FF5E00]/25 bg-[#FFF7ED]"
                : "border-[#E5E5E5] bg-[#FAFAFA]";
          return (
            <div
              key={key}
              className={`min-w-0 rounded-lg border p-3 ${cardClass}`}
            >
              <div className="break-words text-sm font-semibold leading-5 text-[#171717] [overflow-wrap:anywhere]">
                {component.title}
              </div>
              {component.summary ? (
                <p className="mt-1 break-words text-sm leading-5 text-[#404040] [overflow-wrap:anywhere]">
                  {component.summary}
                </p>
              ) : null}
              {component.items?.length ? (
                component.type === "numbered_list" ? (
                  <ol className="mt-2 list-decimal space-y-1 pl-4 text-sm leading-5 text-[#404040] marker:text-[#A3A3A3]">
                    {component.items.map((item, itemIndex) => (
                      <li
                        key={`${key}-${itemIndex}`}
                        className="break-words [overflow-wrap:anywhere]"
                      >
                        {formatStructuredDisplayValue(item)}
                      </li>
                    ))}
                  </ol>
                ) : (
                  <ul className="mt-2 list-disc space-y-1 pl-4 text-sm leading-5 text-[#404040] marker:text-[#A3A3A3]">
                    {component.items.map((item, itemIndex) => (
                      <li
                        key={`${key}-${itemIndex}`}
                        className="break-words [overflow-wrap:anywhere]"
                      >
                        {formatStructuredDisplayValue(item)}
                      </li>
                    ))}
                  </ul>
                )
              ) : null}
            </div>
          );
        })}
      </div>
    );
  };

  const renderStructuredContext = (
    context: DataChatStructuredPayload | null | undefined,
  ) => {
    const payload = context || emptyStructuredPayload();
    const visibleComponents = payload.components.filter(
      shouldRenderAnswerComponent,
    );
    const hasComponents = visibleComponents.length > 0;
    const hasEvidence = payload.evidence.length > 0;
    const hasFollowUps = payload.followUpQuestions.length > 0;
    if (!hasComponents && !hasEvidence && !hasFollowUps) {
      return null;
    }
    return (
      <div className="mt-4 space-y-3">
        {hasComponents ? renderAnswerComponents(visibleComponents) : null}

        {hasEvidence ? (
          <div className="grid min-w-0 gap-2 sm:grid-cols-2">
            {payload.evidence.map((item, evidenceIndex) => (
              <div
                key={`${item.label}-${evidenceIndex}`}
                className="min-w-0 rounded-lg border border-[#E5E5E5] bg-white px-3 py-2"
              >
                <div className="text-[11px] font-medium uppercase leading-4 tracking-normal text-[#737373]">
                  {item.label}
                </div>
                <div className="mt-1 break-words text-sm font-semibold leading-5 text-[#171717] [overflow-wrap:anywhere]">
                  {item.value}
                </div>
                {item.detail ? (
                  <div className="mt-1 line-clamp-2 break-words text-xs leading-5 text-[#737373] [overflow-wrap:anywhere]">
                    {item.detail}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        ) : null}

        {hasFollowUps ? (
          <Suggestions className="pt-1">
            {payload.followUpQuestions
              .map(stripSuggestedActionPunctuation)
              .filter(Boolean)
              .slice(0, 4)
              .map((question) => (
                <Suggestion
                  key={question}
                  onClick={() => {
                    trackAgentEvent("agent_suggested_prompt_clicked", {
                      question_type: "follow_up",
                      suggested_prompt: question,
                    });
                    submitMessage(question);
                  }}
                  disabled={Boolean(currentPendingRequest)}
                  className="rounded-full"
                >
                  {question}
                </Suggestion>
              ))}
          </Suggestions>
        ) : null}
      </div>
    );
  };

  const copyAnswer = async (content: string, index: number) => {
    try {
      await navigator.clipboard?.writeText(content);
      trackAgentEvent("agent_answer_copied", { message_id: `local-${index}` });
      markActionCopied(`answer-${index}`);
    } catch {
      toast.error(t("dataChat.toast.copyAnswerFailed"));
    }
  };

  const copyMessage = async (content: string, index: number) => {
    try {
      await navigator.clipboard?.writeText(content);
      markActionCopied(`message-${index}`);
    } catch {
      toast.error(t("dataChat.toast.copyMessageFailed"));
    }
  };

  const startEditingMessage = (index: number, content: string) => {
    if (currentPendingRequest) return;
    setEditingMessageIndex(index);
    setEditingDraft(content);
  };

  const cancelEditingMessage = () => {
    setEditingMessageIndex(null);
    setEditingDraft("");
  };

  const sendEditedMessage = () => {
    if (editingMessageIndex === null || currentPendingRequest) return;
    const nextMessage = editingDraft.trim();
    if (!nextMessage) return;
    const messageIndex = editingMessageIndex;
    cancelEditingMessage();
    setMessages((current) => current.slice(0, messageIndex));
    submitMessage(nextMessage);
  };

  const retryAnswer = (index: number) => {
    if (currentPendingRequest) return;
    const previousUser = [...messages]
      .slice(0, index)
      .reverse()
      .find((message) => message.role === "user");
    const targetAssistant = messages[index];
    if (!previousUser) return;
    if (!targetAssistant?.id) {
      toast.error(t("dataChat.toast.reloadBeforeRetry"));
      return;
    }
    trackAgentEvent("agent_answer_retried", {
      message_id: targetAssistant.id,
    });
    setMessages((current) => current.slice(0, index));
    submitMessage(previousUser.content, {
      appendUserMessage: false,
      rerunFromMessageId: targetAssistant.id,
    });
  };

  const ChatLoadingState = () => (
    <div
      role="status"
      aria-live="polite"
      className="w-fit max-w-[360px] rounded-lg px-1 py-2 text-sm text-[#525252]"
    >
      <div className="flex animate-pulse items-center gap-2">
        <Loader className="text-[#737373]" />
        <span>{t("dataChat.loading.chat")}</span>
      </div>
    </div>
  );

  const AnswerLoadingState = () => (
    <div
      role="status"
      aria-live="polite"
      className="w-fit max-w-[420px] rounded-lg px-1 py-2 text-sm text-[#737373]"
    >
      <div className="space-y-2.5">
        {answerLoadingSteps.map((step, index) => {
          const isComplete = index < answerLoadingStep;
          const isActive = index === answerLoadingStep;
          return (
            <div
              key={step}
              className={`flex items-center gap-2 ${
                isActive
                  ? "animate-pulse text-[#525252]"
                  : isComplete
                    ? "text-[#525252]"
                    : "text-[#A3A3A3]"
              }`}
              style={{ animationDelay: `${index * 90}ms` }}
            >
              {isComplete ? (
                <Check className="h-4 w-4 shrink-0 text-[#16A34A]" />
              ) : isActive ? (
                <Loader className="shrink-0 text-[#A3A3A3]" />
              ) : (
                <span className="ml-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[#D4D4D4]" />
              )}
              <span>{step}</span>
            </div>
          );
        })}
        <div className="relative mt-3 h-0.5 w-full max-w-[260px] overflow-hidden rounded-full bg-[#F5F5F5]">
          <div className="absolute inset-y-0 left-0 w-1/3 animate-[agent-progress_1.2s_ease-in-out_infinite] rounded-full bg-[#FF5E00]" />
        </div>
        {currentPendingRequest &&
        currentPendingRequest.completedTools !== null &&
        currentPendingRequest.totalTools !== null ? (
          <div className="text-xs text-[#A3A3A3]">
            {t("dataChat.loading.sourcesCompleted", {
              completed: currentPendingRequest.completedTools,
              total: currentPendingRequest.totalTools,
            })}
          </div>
        ) : null}
      </div>
      <style>
        {`@keyframes agent-progress {
          0% { transform: translateX(-120%); opacity: 0.25; }
          35% { opacity: 1; }
          100% { transform: translateX(320%); opacity: 0.25; }
        }`}
      </style>
    </div>
  );

  const renderPromptInput = (variant: "hero" | "dock") => {
    const isResponseInProgress =
      Boolean(currentPendingRequest) || isAnswerRevealing;
    const canStopResponse =
      Boolean(currentPendingRequest?.abortController) || isAnswerRevealing;
    const canSendMessage = Boolean(input.trim()) && !isResponseInProgress;

    return (
      <TooltipProvider>
        <PromptInput
          className={
            variant === "hero"
              ? "min-h-[124px] rounded-[24px] border-[#E5E5E5] p-4 shadow-[0_1px_3px_rgba(0,0,0,0.10)]"
              : "min-h-[124px] rounded-[24px] border-[#E5E5E5] p-4 shadow-[0_10px_24px_rgba(0,0,0,0.08)]"
          }
          onSubmit={(event) => {
            event.preventDefault();
            if (isResponseInProgress && canStopResponse) {
              stopCurrentResponse();
              return;
            }
            submitMessage();
          }}
        >
          <PromptInputTextarea
            value={input}
            onChange={(event) => setInput(event.currentTarget.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                submitMessage();
              }
            }}
            disabled={isResponseInProgress}
            placeholder={
              variant === "hero"
                ? placeholderText
                : t("dataChat.placeholder.ask")
            }
            className={
              variant === "hero"
                ? "min-h-[52px] text-base leading-6"
                : "min-h-[52px] text-base leading-6"
            }
          />
          <PromptInputToolbar className={variant === "hero" ? "pt-4" : "pt-2"}>
            <PromptInputTools>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <PromptInputButton
                      aria-label={t("dataChat.attachSoon")}
                      title={t("dataChat.soon")}
                      disabled
                      className="h-8 w-8 rounded-md border-0 bg-[#FAFAFA] p-0 text-[#737373] shadow-none hover:bg-[#F5F5F5] disabled:cursor-not-allowed disabled:opacity-100"
                    >
                      <Plus className="h-4 w-4" />
                    </PromptInputButton>
                  </span>
                </TooltipTrigger>
                <TooltipContent side="top">{t("dataChat.soon")}</TooltipContent>
              </Tooltip>
            </PromptInputTools>
            <PromptInputSubmit
              type={isResponseInProgress ? "button" : "submit"}
              status={isResponseInProgress ? "submitted" : "ready"}
              disabled={
                isResponseInProgress ? !canStopResponse : !input.trim()
              }
              onClick={
                isResponseInProgress && canStopResponse
                  ? (event) => {
                      event.preventDefault();
                      stopCurrentResponse();
                    }
                  : undefined
              }
              className={
                canSendMessage
                  ? "h-8 w-8 cursor-pointer rounded-md bg-[#FF5E00] text-white shadow-none hover:bg-[#EA580C] disabled:cursor-not-allowed disabled:opacity-100"
                  : "h-8 w-8 cursor-pointer rounded-md bg-[#FAFAFA] text-[#A3A3A3] shadow-none hover:bg-[#F5F5F5] hover:text-[#737373] disabled:cursor-not-allowed disabled:bg-[#FAFAFA] disabled:text-[#A3A3A3] disabled:opacity-100"
              }
            />
          </PromptInputToolbar>
        </PromptInput>
      </TooltipProvider>
    );
  };

  const hasConversation =
    messages.length > 0 ||
    Boolean(currentPendingRequest) ||
    threadQuery.isLoading;

  if (!hasConversation) {
    return (
      <div className="flex h-full min-h-[680px] flex-col items-center justify-start px-4 pb-14 pt-[136px]">
        <div className="w-full max-w-[800px]">
          <div className="mb-8 flex flex-col items-center text-center">
            <img
              src={agentLogo}
              alt={t("legacy.nobori.1aqw71u")}
              className="mb-6 h-16 w-16"
            />
            <h1 className="text-[32px] font-bold leading-[40px] tracking-normal text-[#0F0F0F]">
              {t("dataChat.hero.title")}
            </h1>
            <p className="mt-1.5 text-base text-[#171717]">
              {t("dataChat.hero.subtitle")}
            </p>
          </div>

          {renderPromptInput("hero")}

          <div className="my-4 text-center text-[11px] text-[#737373]">
            {t("dataChat.hero.startWith")}
          </div>

          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
            {starterSuggestions.map((suggestion) => {
              const label = t(suggestion.labelKey);
              return (
                <button
                  key={suggestion.labelKey}
                  type="button"
                  onClick={() => {
                    trackAgentEvent("agent_suggested_prompt_clicked", {
                      question_type: label,
                    });
                    submitMessage(suggestion.prompt);
                  }}
                  disabled={Boolean(currentPendingRequest)}
                  className="flex h-[92px] flex-col items-start justify-between rounded-[12px] border border-[#E5E5E5] bg-white p-3 text-left font-semibold text-[#0A0A0A] transition-colors hover:border-[#D4D4D4] hover:bg-[#FAFAFA] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF5E00]/30 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <img
                    src={suggestion.icon}
                    alt=""
                    aria-hidden="true"
                    className="h-5 w-5"
                  />
                  <span className="break-words text-[13px] leading-5">
                    {label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto flex h-full min-h-[680px] w-full max-w-[994px] flex-col">
      <Conversation className="min-h-0 flex-1 bg-transparent">
        <ConversationContent className="space-y-8 px-0 py-4">
          {threadQuery.isLoading ? <ChatLoadingState /> : null}
          {messages.map((message, index) => (
            <Message from={message.role} key={`${message.role}-${index}`}>
              {message.role === "assistant" ? (
                <MessageContent
                  from="assistant"
                  className="max-w-[800px] border-0 bg-transparent p-0 text-base leading-6"
                >
                  <div className="min-w-0">
                    {renderAssistantMarkdown(message.content)}
                    {message.isGenerating ? (
                      <span
                        aria-hidden="true"
                        className="ml-0.5 inline-block h-5 w-1 animate-pulse rounded-full bg-[#171717] align-[-3px]"
                      />
                    ) : null}
                  </div>
                  {message.isGenerating
                    ? null
                    : renderStructuredContext(message.context)}
                  {!message.isGenerating ? (
                    <div className="mt-3 flex items-center gap-1 border-t border-[#F5F5F5] pt-2">
                      <MessageActionButton
                        label={
                          copiedActionId === `answer-${index}`
                            ? t("dataChat.action.copiedResponse")
                            : t("dataChat.action.copyResponse")
                        }
                        onClick={() => copyAnswer(message.content, index)}
                      >
                        {copiedActionId === `answer-${index}` ? (
                          <Check className="h-4 w-4 text-[#16A34A]" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </MessageActionButton>
                      <MessageActionButton
                        label={t("dataChat.action.tryAgain")}
                        onClick={() => retryAnswer(index)}
                        disabled={Boolean(currentPendingRequest)}
                      >
                        <RefreshCcw className="h-3.5 w-3.5" />
                      </MessageActionButton>
                      <MessageActionButton label={t("dataChat.soon")} disabled>
                        <Share2 className="h-4 w-4" />
                      </MessageActionButton>
                    </div>
                  ) : null}
                </MessageContent>
              ) : (
                <div className="flex max-w-[760px] flex-col items-end">
                  <MessageContent
                    from="user"
                    className={
                      editingMessageIndex === index
                        ? "max-w-none bg-transparent p-0"
                        : undefined
                    }
                  >
                    {editingMessageIndex === index ? (
                      <div className="min-w-[min(720px,calc(100vw-3rem))] rounded-[28px] bg-[#F5F5F5] p-5">
                        <textarea
                          ref={editingTextareaRef}
                          value={editingDraft}
                          onChange={(event) =>
                            setEditingDraft(event.currentTarget.value)
                          }
                          onKeyDown={(event) => {
                            if (event.key === "Enter" && !event.shiftKey) {
                              event.preventDefault();
                              sendEditedMessage();
                            }
                          }}
                          autoFocus
                          className="min-h-[80px] w-full resize-none overflow-hidden border-0 bg-transparent text-base leading-6 text-[#171717] outline-none"
                        />
                        <div className="mt-4 flex justify-end gap-2">
                          <button
                            type="button"
                            onClick={cancelEditingMessage}
                            className="h-9 rounded-full bg-white px-4 text-sm font-semibold text-[#0A0A0A] hover:bg-[#F5F5F5]"
                          >
                            {t("dataChat.action.cancel")}
                          </button>
                          <button
                            type="button"
                            onClick={sendEditedMessage}
                            disabled={
                              !editingDraft.trim() ||
                              Boolean(currentPendingRequest)
                            }
                            className="h-9 rounded-full bg-[#0A0A0A] px-4 text-sm font-semibold text-white hover:bg-[#171717] disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            {t("dataChat.action.send")}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <p className="m-0 break-words whitespace-pre-wrap [overflow-wrap:anywhere]">
                        {message.content}
                      </p>
                    )}
                  </MessageContent>
                  {editingMessageIndex !== index ? (
                    <div className="mt-2 flex justify-end gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                      <MessageActionButton
                        label={
                          copiedActionId === `message-${index}`
                            ? t("dataChat.action.copiedMessage")
                            : t("dataChat.action.copyMessage")
                        }
                        onClick={() => copyMessage(message.content, index)}
                      >
                        {copiedActionId === `message-${index}` ? (
                          <Check className="h-4 w-4 text-[#16A34A]" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </MessageActionButton>
                      <MessageActionButton
                        label={t("dataChat.action.editMessage")}
                        onClick={() =>
                          startEditingMessage(index, message.content)
                        }
                        disabled={Boolean(currentPendingRequest)}
                      >
                        <Pencil className="h-4 w-4" />
                      </MessageActionButton>
                    </div>
                  ) : null}
                </div>
              )}
            </Message>
          ))}
          {currentPendingRequest ? <AnswerLoadingState /> : null}
          <div ref={conversationEndRef} aria-hidden="true" />
        </ConversationContent>
      </Conversation>

      <div className="pb-3">{renderPromptInput("dock")}</div>
    </div>
  );
}
